import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Download, Eye, Edit, Phone, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePagination, useFilters, useSearch, useCrud, useApiQuery } from '../../hooks/useApi';
import { contactService, identityService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import ContactModal from '../../components/Contact/ContactModal';
import ContactTable from '../../components/Contact/ContactTable';
import ContactFilters from '../../components/Contact/ContactFilters';
import { downloadJson } from '../../utils/exportHelpers';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { fetchAllPages } from '../../utils/apiPagination';

const toBool = (v) => v === true || v === 'true';

function mapContact(c, personById) {
  const pid = c.person_id != null ? String(c.person_id) : '';
  const p = pid ? personById.get(pid) : null;
  return {
    id: String(c.id),
    personId: pid,
    personName: p?.name || (pid ? `${pid.slice(0, 8)}…` : '—'),
    contactType: c.contact_type,
    value: c.value,
    description: c.label || c.description || '',
    isActive: c.is_active,
    createdAt: c.created_at,
  };
}

const Contacts = () => {
  const { t } = useTranslation('contact');

  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const pagination = usePagination(1, 10);
  const filters = useFilters({
    contactType: '',
    personId: '',
    isActive: '',
  });
  const search = useSearch();

  const { data: personsData } = useApiQuery(['identity-persons', 'contacts-page'], () =>
    fetchAllPages((params) => identityService.getPersons(params))
  );

  const persons = useMemo(() => {
    const d = personsData;
    const arr = Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
    return arr;
  }, [personsData]);

  const personOptions = useMemo(
    () => persons.map((p) => ({ id: String(p.id), name: p.name || String(p.id) })),
    [persons]
  );

  const personById = useMemo(() => new Map(persons.map((p) => [String(p.id), p])), [persons]);

  const { useList, useCreate, useUpdate, useDelete } = useCrud('contacts', contactService.contactCrud);
  const { data: listData, isLoading, error, refetch } = useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const rawList = useMemo(() => {
    const d = listData;
    return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
  }, [listData]);

  const mapped = useMemo(() => rawList.map((c) => mapContact(c, personById)), [rawList, personById]);

  const filtered = useMemo(() => {
    let rows = mapped;
    if (filters.filters.contactType) {
      rows = rows.filter((r) => r.contactType === filters.filters.contactType);
    }
    if (filters.filters.personId) {
      rows = rows.filter((r) => r.personId === filters.filters.personId);
    }
    if (filters.filters.isActive === 'true') rows = rows.filter((r) => r.isActive === true);
    if (filters.filters.isActive === 'false') rows = rows.filter((r) => r.isActive === false);
    const q = (search.query || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          (r.value || '').toLowerCase().includes(q) ||
          (r.personName || '').toLowerCase().includes(q) ||
          (r.description || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [mapped, filters.filters, search.query]);

  useEffect(() => {
    pagination.setTotal(filtered.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  const paged = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filtered.slice(start, start + pagination.limit);
  }, [filtered, pagination.page, pagination.limit]);

  const handleCreateContact = () => {
    setSelectedContact(null);
    setShowModal(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const handleViewContact = (contact) => {
    toast(
      [
        `Pessoa: ${contact.personName}`,
        `Tipo: ${contact.contactType}`,
        `Valor: ${contact.value}`,
        contact.description ? `Descrição: ${contact.description}` : '',
      ]
        .filter(Boolean)
        .join('\n'),
      { duration: 6000 }
    );
  };

  const handleDeleteContact = (contact) => {
    if (!window.confirm('Excluir este contato?')) return;
    deleteMutation.mutate(contact.id, {
      onSuccess: () => {
        setShowModal(false);
        setSelectedContact(null);
      },
      onError: (e) => toast.error(userFacingError(e, 'deleteError')),
    });
  };

  const handleSave = (data) => {
    if (selectedContact) {
      updateMutation.mutate(
        {
          id: selectedContact.id,
          data: {
            person_id: data.personId,
            contact_type: data.contactType,
            value: data.value,
            label: data.description || undefined,
            is_active: toBool(data.isActive),
          },
        },
        {
          onSuccess: () => {
            setShowModal(false);
            setSelectedContact(null);
          },
          onError: (e) => toast.error(userFacingError(e, 'updateError')),
        }
      );
    } else {
      createMutation.mutate(
        {
          person_id: data.personId,
          contact_type: data.contactType,
          value: data.value,
          label: data.description || undefined,
          is_primary: false,
          is_active: true,
        },
        {
          onSuccess: () => setShowModal(false),
          onError: (e) => toast.error(userFacingError(e, 'createError')),
        }
      );
    }
  };

  const handleExport = () => {
    downloadJson(`contatos-${new Date().toISOString().slice(0, 10)}.json`, filtered);
    toast.success('Exportação iniciada');
  };

  const getStatusBadge = (isActive) => (
    <span className={`badge ${isActive ? 'badge-success' : 'badge-danger'}`}>{isActive ? 'Ativo' : 'Inativo'}</span>
  );

  const getContactTypeBadge = (contactType) => {
    const typeConfig = {
      email: { label: 'Email', color: 'info', icon: Mail },
      phone: { label: 'Telefone', color: 'primary', icon: Phone },
      whatsapp: { label: 'WhatsApp', color: 'success', icon: Phone },
    };
    const config = typeConfig[contactType] || { label: contactType || '—', color: 'secondary', icon: Mail };
    const Icon = config.icon;
    return (
      <span className={`badge badge-${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (error) {
    return (
      <FormPageShell>
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-danger-800">
          {t('common:loadError')}{' '}
          <button type="button" className="btn btn-outline btn-sm ml-2" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("title")}
          description="Telefones e e-mails vinculados a pessoas cadastradas."
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Contatos' }]}
        >
          <Can permission={P.CONTACT_ACCESS}>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button type="button" className="btn btn-outline shadow-sm" onClick={handleExport} disabled={!filtered.length}>
                <Download className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
              <button
                type="button"
                onClick={handleCreateContact}
                className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
              >
                <Plus className="h-4 w-4" />
                Novo contato
              </button>
            </div>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <ContactFilters filters={filters} search={search} personOptions={personOptions} />
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            {isLoading && <p className="text-sm text-gray-600">Carregando…</p>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">Mostrando {filtered.length} contato(s)</div>
            </div>

            {viewMode === 'table' ? (
              <ContactTable
                contacts={paged}
                totalCount={filtered.length}
                pagination={pagination}
                onView={handleViewContact}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                getStatusBadge={getStatusBadge}
                getContactTypeBadge={getContactTypeBadge}
                onCreate={handleCreateContact}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {paged.map((contact) => (
                  <div key={contact.id} className="card">
                    <div className="card-content">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                            {contact.contactType === 'email' ? <Mail className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{contact.personName}</h3>
                            <p className="text-sm text-gray-600">{contact.value}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(contact.isActive)}
                          {getContactTypeBadge(contact.contactType)}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="text-sm text-gray-700">{contact.description}</div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Criado em:</span>
                          <span className="text-gray-900">
                            {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString('pt-BR') : '—'}
                          </span>
                        </div>
                      </div>

                      <Can permission={P.CONTACT_ACCESS}>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleViewContact(contact)} className="btn btn-outline btn-sm flex-1">
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </button>
                          <button type="button" onClick={() => handleEditContact(contact)} className="btn btn-outline btn-sm flex-1">
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </button>
                        </div>
                      </Can>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <ContactModal
          key={selectedContact?.id ?? 'new'}
          contact={selectedContact}
          personOptions={personOptions}
          onClose={() => {
            setShowModal(false);
            setSelectedContact(null);
          }}
          onSave={handleSave}
        />
      )}
    </FormPageShell>
  );
};

export default Contacts;
